import React, { useState } from "react";
import { TabsWithFab } from "@/components/shared/TabsWithFab";
import { useToast } from "@stackje/shared-components/hooks/useToast";
import { captureErrorAndToast } from "@/utils/error-utils";
import { useNavigate } from "@tanstack/react-router";

interface AddCardContentProps {
  switchSubTypeSelection?: () => void;
  previewContent: React.ReactNode;
  formContent: React.ReactNode;
  switchThemeSelection?: () => void;
  onFormInputChange?: () => void;
  onSubmit?: (formData: FormData) => Promise<void>;
  handleSubmit: (onSubmit: (data: Record<string, unknown>) => void | Promise<void>) => (e?: React.BaseSyntheticEvent) => Promise<void>;
}

export function AddCardContent({
  switchSubTypeSelection,
  previewContent,
  formContent,
  switchThemeSelection,
  onFormInputChange,
  onSubmit,
  handleSubmit,
}: AddCardContentProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createFormDataFromObject = (data: Record<string, unknown>): FormData => {
    const formData = new FormData();
    
    // Special keys that should be JSON stringified instead of flattened
    const jsonKeys = new Set(['colors', 'amounts']);
    
    // Flatten nested objects with dot notation for FormData compatibility
    const flattenObject = (obj: Record<string, unknown>, prefix = ''): void => {
      for (const [key, value] of Object.entries(obj)) {
        if (value === null || value === undefined) continue;
        
        const fullKey = prefix ? `${prefix}.${key}` : key;
        
        // Special handling for array fields - convert to indexed fields
        if (key === 'faqs' && Array.isArray(value)) {
          value.forEach((faq: { question?: string; answer?: string }, index: number) => {
            formData.set(`faqQuestion${index}`, String(faq.question || ''));
            formData.set(`faqAnswer${index}`, String(faq.answer || ''));
          });
        } else if (key === 'images' && Array.isArray(value)) {
          value.forEach((img: { image?: { src?: string; alt?: string }; caption?: string }, index: number) => {
            formData.set(`imageSrc${index}`, String(img.image?.src || ''));
            formData.set(`imageAlt${index}`, String(img.image?.alt || ''));
            formData.set(`imageCaption${index}`, String(img.caption || ''));
          });
        } else if (key === 'links' && Array.isArray(value)) {
          value.forEach((link: { title?: string; url?: string; description?: string; image?: { src?: string; alt?: string } }, index: number) => {
            formData.set(`linkTitle${index}`, String(link.title || ''));
            formData.set(`linkUrl${index}`, String(link.url || ''));
            formData.set(`linkDescription${index}`, String(link.description || ''));
            formData.set(`linkImageSrc${index}`, String(link.image?.src || ''));
            formData.set(`linkImageAlt${index}`, String(link.image?.alt || ''));
          });
        } else if (key === 'features' && Array.isArray(value)) {
          value.forEach((feature: { title?: string; description?: string; image?: string }, index: number) => {
            formData.set(`featureTitle${index}`, String(feature.title || ''));
            formData.set(`featureDescription${index}`, String(feature.description || ''));
            formData.set(`featureImage${index}`, String(feature.image || ''));
          });
        }
        // Check if this key should be JSON stringified
        else if (jsonKeys.has(key)) {
          formData.set(fullKey, JSON.stringify(value));
        } else if (typeof value === 'object' && !Array.isArray(value)) {
          // Recursively flatten nested objects (except special keys)
          flattenObject(value as Record<string, unknown>, fullKey);
        } else {
          // Set primitive values or arrays (arrays get JSON.stringify)
          if (Array.isArray(value)) {
            formData.set(fullKey, JSON.stringify(value));
          } else {
            formData.set(fullKey, String(value));
          }
        }
      }
    };
    
    flattenObject(data);
    return formData;
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Track if the submission was successful
      let submissionSuccessful = false;
      
      // Use react-hook-form's handleSubmit for validation
      await handleSubmit(async (validatedData) => {
        const formData = createFormDataFromObject(validatedData);
        await onSubmit?.(formData);
        submissionSuccessful = true;
      })(e);
      
      // If we reach here and submission was successful, show success toast
      if (submissionSuccessful) {
        toast({
          description: "Card added successfully",
        });
        navigate({ to: "/" });
      } else {
        // Validation failed, show error toast
        toast({
          description: "Please fix the validation errors and try again.",
          variant: "destructive",
        });
      }
    } catch (e) {
      captureErrorAndToast(e, toast, {
        description: `${e}`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <TabsWithFab
      formContent={formContent}
      previewContent={previewContent}
      switchSubTypeSelection={switchSubTypeSelection}
      switchThemeSelection={switchThemeSelection}
      onFormInputChange={onFormInputChange}
      onSubmit={handleFormSubmit}
      isSubmitting={isSubmitting}
      formId="add-form"
      submitLabel="PUBLISH"
    />
  );
}
